import { ActivatedRoute, Route, Router, Routes } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { LoginService } from 'src/app/login/services/login-service.service';

import { BookmarkNavComponent } from '../bookmark-nav/bookmark-nav.component';
import { MenuNavComponent } from '../menu-nav/menu-nav.component';
import { NotFoundPageComponent } from '../not-found-page/not-found-page.component';
import { DefaultThemeComponent } from '../themes/default/default.component';
import { IBookmarkEntry } from './bookmark-entry.interface';
import { IMenuItem } from './menu-item.interface';

function routeDataItemToRoute(
    routeData: IMenuItem,
    path?: string,
    parent?: Route,
    userRoles?: string[]
): Route[] {
    const result = {
        path: path ? `${path}/${routeData.path}` : routeData.path,
        component: routeData.moduleItem
            ? MenuNavComponent
            : BookmarkNavComponent,
        resolve: routeData.resolve
            ? { myResolver: routeData.resolve }
            : undefined,
        data: {
            id: routeData.id,
            description: routeData.description,
            subMenu: routeData.subMenu,
            path: routeData.path,
            component: routeData.component,
            parentMenu: parent,
            dynamic: true,
            bookmarkPath: routeData.bookmarkPath,
            custom: routeData.data,
        },
        children: undefined,
    };
    result.children = routeData.moduleItem
        ? [...routeDataToRoute([], routeData.subMenu, undefined, result)]
        : [];

    const accessRouteData = result.children.filter(
        row =>
            !row.data.needsRole ||
            (userRoles &&
                row.data.needsRole.some(role => userRoles.indexOf(role) >= 0))
    );

    if (accessRouteData.length > 0) {
        result.children = [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: accessRouteData[accessRouteData.length - 1].path,
            },
            ...result.children,
        ];
    }

    let childData = [];
    if (!routeData.moduleItem) {
        childData = routeDataToRoute(
            [],
            routeData.subMenu,
            path ? `${path}/${routeData.path}` : routeData.path,
            result
        );
    }

    return [...childData, result];
}

function routeDataToRoute(
    startConfig: Routes,
    routeData: IMenuItem[],
    path?: string,
    parent?: Route,
    userRoles?: string[],
    addDefaultRoute?: boolean
): Routes {
    if (routeData === undefined) {
        return [];
    }

    const topNavRoutes = routeData.reduce(
        (acc, row) => [
            ...routeDataItemToRoute(row, path, parent, userRoles),
            ...acc,
        ],
        []
    );

    const blankRoute = [];
    const defaultModule = [];
    const accessRouteData = routeData.filter(
        data =>
            !data.needsRole ||
            (userRoles &&
                data.needsRole.some(role => userRoles.indexOf(role) >= 0))
    );
    if (accessRouteData.length > 0 && !path && addDefaultRoute) {
        // console.warn('DEFAULT', routeData);

        const defRouteBlank: Route = {
            path: '',
            pathMatch: 'full',
            redirectTo: `${accessRouteData[0].path}`,
            data: { dynamic: true },
        };
        blankRoute.push(defRouteBlank);

        const defRoute: Route = {
            path: '**',
            // redirectTo: `${accessRouteData[0].path}`
            component: NotFoundPageComponent,
            data: { dynamic: true },
        };
        defaultModule.push(defRoute);
    }

    const topNavApplyTheme = !parent
        ? [
              {
                  path: '',
                  component: DefaultThemeComponent,
                  children: [...topNavRoutes],
                  data: { dynamic: true },
              },
          ]
        : topNavRoutes;

    const finalRoute = [
        ...blankRoute,
        ...topNavApplyTheme,
        ...startConfig,
        ...defaultModule,
    ];
    // console.log('Route test', finalRoute);

    return finalRoute;
}

export abstract class TopnavInfoService {
    private reloadRoute$ = new BehaviorSubject<boolean>(true);
    private bookmarkData$ = new BehaviorSubject<IBookmarkEntry[]>([]);

    public routesUpdated$ = new Subject();

    public objectStoreChanged$ = new BehaviorSubject(null);

    constructor(
        private routerSuper: Router,
        routeData: Observable<IMenuItem[]>,
        private loginServce: LoginService
    ) {
        loginServce.loggedIn$.subscribe(v => {
            // console.log('User logged in, resetting nav for new user');
            this.loadRoutes();
        });

        loginServce.loggedOut$.subscribe(v => {
            this.clearRouteData();
        });

        combineLatest([routeData, this.reloadRoute$])
            .pipe(
                map(([v, _]) => v),
                withLatestFrom(this.getUserRoles())
            )
            .subscribe(([data, roles]) => {
                this.clearRouteData();
                this.routerSuper.resetConfig(
                    routeDataToRoute(
                        this.routerSuper.config,
                        data,
                        undefined,
                        undefined,
                        roles,
                        true
                    )
                );
                // console.log('New Route:', this.routerSuper.config);
                this.routesUpdated$.next(this.routerSuper.config);
            });
    }

    public loadRoutes() {
        this.reloadRoute$.next(true);
    }

    public clearRouteData() {
        // console.log('Clearing Dynamic routes');
        const config = this.routerSuper.config.filter(
            row => !row.data || !row.data.dynamic
        );
        this.routerSuper.resetConfig([
            ...config,
            {
                path: '',
                redirectTo: 'Loading',
                pathMatch: 'full',
                data: { dynamic: true },
            },
        ]);
        // console.log('Done Dynamic routes', this.routerSuper.config);
    }

    public getModuleName(route: ActivatedRoute): Observable<string> {
        return route.data.pipe(
            map(data => data.description),
            filter(mod => mod !== undefined)
        );
    }

    public getModulePath(route: ActivatedRoute): Observable<string> {
        return route.data.pipe(
            map(data => data.path),
            filter(mod => mod !== undefined)
        );
    }

    public addExtraBreadCrumbData(values: IBookmarkEntry[]) {
        this.bookmarkData$.next(values);
    }

    public getExtraBreadcrumb(): Observable<IBookmarkEntry[]> {
        return this.bookmarkData$.asObservable();
    }

    abstract getLogoFilename(): Observable<string>;
    abstract getUserName(): Observable<string>;
    abstract getUserRoles(): Observable<string[]>;
    abstract getModules(): Observable<IMenuItem[]>;
}
